import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import CandidateLayout from './pages/Candidate/CandidateLayout';
import CandidateProfile from './pages/Candidate/Profile';
import CandidateBusinessView from './pages/Candidate/BusinessView';
import CandidateJobsView from './pages/Candidate/Jobs';
import CandidateJobApply from './pages/Candidate/JobApply';
import NotFound from './pages/404';
import ErrorPage from './pages/ErrorPage';
import CandidateSettingsView from './pages/Candidate/Settings';
import HomeLayout from './pages/HomeLayout';
import HomeBody from './pages/HomeBody';
import JobSearch from './pages/JobSearch';
import Jobs from './pages/Jobs';
import JobDetail from './pages/Job';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { UserProvider } from './Context/UserContext';
import { TokenProvider } from './Context/TokenContext';
import { CandidateProvider, useCandidate } from './Context/CandidateContext';
import { AlertProvider } from './Context/AlertContext';
import AuthCallback from './components/AuthCallback';
import ProtectedRoute from './utils/ProtectedRoute';
import AdditionalInfo from './pages/Candidate/CandidateInfoPage';
import { HelmetProvider, Helmet } from 'react-helmet-async';

import 'bootstrap/dist/css/bootstrap.min.css';
import './css/main.css';
import './css/tokens.css';
import './css/utilities.css';
import './js/main.js';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './css/talentup.css';

function Home() {
    return (
        <HomeLayout>
            <HomeBody />
        </HomeLayout>
    );
}

// Blocks all candidate routes until the candidate has a profile in the DB.
// While data is loading, shows a spinner. Once loaded, if no profile exists,
// redirects to /candidate/additional-info regardless of what URL was attempted.
function CandidateRegistrationGuard({ children }) {
    const { apiData, fetching, isLoading } = useCandidate();

    if (fetching || isLoading) {
        return <div className="loadingStyle">Loading...</div>;
    }

    if (!apiData) {
        return <Navigate to="/candidate/additional-info" replace />;
    }

    return children;
}

function AuthenticatedCandidate() {
    return (
        <CandidateRegistrationGuard>
            <CandidateLayout>
                <Routes>
                    <Route path="/profile" element={<CandidateProfile />} />
                    <Route path="/businessview" element={<CandidateBusinessView />} />
                    <Route path="/jobs" element={<CandidateJobsView />} />
                    <Route path="/settings" element={<CandidateSettingsView />} />
                </Routes>
            </CandidateLayout>
        </CandidateRegistrationGuard>
    );
}

function JobApplication() {
    return (
        <HomeLayout>
            <Routes>
                <Route path="/:jobId" element={<CandidateJobApply />} />
            </Routes>
        </HomeLayout>
    );
}

const DefaultHelmet = () => (
    <Helmet>
        <title>Find Jobs In Tech | Stacktalent.com.au</title>
        <meta name="description" content="Stacktalent is Australia's premier IT job portal, connecting top tech talent with leading employers. Find the best IT jobs and get hired by top companies in software development, cybersecurity, cloud computing, and more. Start your IT career journey today" />
    </Helmet>
);

function App() {
    return (
        <HelmetProvider>
            <DefaultHelmet />
            <AlertProvider>
                <TokenProvider>
                    <UserProvider>
                        <CandidateProvider>
                            <Routes>
                                <Route path="/" element={<Home />} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/auth/callback" element={<AuthCallback />} />
                                <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                                <Route path="/auth/reset-password" element={<ResetPassword />} />
                                <Route path="/job-search" element={<JobSearch />} />
                                <Route path="/jobs" element={<HomeLayout><Jobs /></HomeLayout>} />
                                <Route path="/job/:jobId" element={<HomeLayout><JobDetail /></HomeLayout>} />
                                <Route path="/jobapply/*" element={<ProtectedRoute><CandidateRegistrationGuard><JobApplication /></CandidateRegistrationGuard></ProtectedRoute>} />
                                <Route path="/candidate/*" element={<ProtectedRoute><AuthenticatedCandidate /></ProtectedRoute>} />
                                <Route path="/candidate/additional-info" element={
                                    <ProtectedRoute>
                                        <AdditionalInfo />
                                    </ProtectedRoute>
                                } />
                                <Route path="/error" element={<ErrorPage />} />
                                <Route path="*" element={<NotFound />} />
                            </Routes>
                        </CandidateProvider>
                    </UserProvider>
                </TokenProvider>
            </AlertProvider>
        </HelmetProvider>
    );
}

export default App;
